/**
 * Página de Comparação Múltipla de Transcrições
 * Compara 2-4 transcrições lado a lado
 */

import React, { useState, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  keyframes
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import BetaBadge from '../../../components/BetaBadge'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PersonIcon from '@mui/icons-material/Person'
import TopicIcon from '@mui/icons-material/Topic'
import TimelineIcon from '@mui/icons-material/Timeline'
import GridViewIcon from '@mui/icons-material/GridView'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import SearchIcon from '@mui/icons-material/Search'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import {
  MultiTranscriptionComparison,
  Contradiction,
  SimilarityPair,
  MatchingSegment
} from '../../../types/transcription'
import { FileItem, compareTranscriptions } from '../../../services/api'
import { contextualErrors, FriendlyError } from '../../../services/errorHandler'
import { useNPS } from '../../../contexts/NPSContext'

// Animações
const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
`

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

const progressBar = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`

type Props = {
  files: FileItem[]
  onBack: () => void
}

const SEVERITY_CONFIG = {
  high: { label: 'Alta', color: '#d32f2f', bgColor: '#ffebee' },
  medium: { label: 'Média', color: '#f57c00', bgColor: '#fff3e0' },
  low: { label: 'Baixa', color: '#388e3c', bgColor: '#e8f5e9' }
}

const FILE_COLORS = ['#1976d2', '#9c27b0', '#00796b', '#f57c00']

function SimilarityBadge({ value }: { value: number }) {
  const percentage = Math.round(value * 100)
  const getColor = () => {
    if (percentage >= 80) return { bg: '#e8f5e9', color: '#2e7d32' }
    if (percentage >= 50) return { bg: '#fff3e0', color: '#f57c00' }
    return { bg: '#ffebee', color: '#c62828' }
  }
  const colors = getColor()

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.5,
        borderRadius: 2,
        bgcolor: colors.bg
      }}
    >
      <Typography variant="body2" fontWeight={700} sx={{ color: colors.color }}>
        {percentage}%
      </Typography>
    </Box>
  )
}

function ContradictionCard({ 
  contradiction, 
  expanded, 
  onToggle,
  fileColors 
}: { 
  contradiction: Contradiction
  expanded: boolean
  onToggle: () => void
  fileColors: Record<string, string>
}) {
  const severity = SEVERITY_CONFIG[contradiction.severity]

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        border: '1px solid #e0e0e0',
        borderLeft: `4px solid ${severity.color}`,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          p: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '&:hover': { bgcolor: '#fafafa' }
        }}
        onClick={onToggle}
      >
        <WarningAmberIcon sx={{ color: severity.color }} />
        
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography variant="subtitle2" fontWeight={600}>
              {contradiction.topic}
            </Typography>
            <Chip
              label={severity.label}
              size="small"
              sx={{
                bgcolor: severity.bgColor,
                color: severity.color,
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20
              }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {contradiction.description}
          </Typography>
        </Box>

        <Chip
          label={`${contradiction.segments.length} versões`}
          size="small"
          variant="outlined"
        />

        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={2}>
            {contradiction.segments.map((segment, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: fileColors[segment.fileId] || '#e0e0e0',
                    borderLeftWidth: 3
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Chip
                      label={segment.fileTitle}
                      size="small"
                      sx={{
                        bgcolor: fileColors[segment.fileId] || '#1976d2',
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.7rem'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {segment.startTime}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                    "{segment.text}"
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon sx={{ fontSize: 14, color: '#666' }} />
                    <Typography variant="caption" color="text.secondary">
                      {segment.speaker}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  )
}

function MatchingSegmentGroup({
  segments,
  fileColors
}: {
  segments: MatchingSegment[]
  fileColors: Record<string, string>
}) {
  return (
    <Paper elevation={0} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
      <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderBottom: '1px solid #c8e6c9' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
          <Typography variant="body2" fontWeight={600} color="#2e7d32">
            Conteúdo Coincidente
          </Typography>
          <SimilarityBadge value={segments[0]?.similarity || 0} />
        </Stack>
      </Box>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {segments.map((seg, idx) => (
            <Grid item xs={12} md={6} lg={4} key={idx}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: fileColors[seg.fileId] || '#e0e0e0',
                  borderLeftWidth: 3,
                  height: '100%'
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: fileColors[seg.fileId] || '#1976d2'
                    }}
                  />
                  <Typography variant="caption" fontWeight={500}>
                    Segmento {seg.segmentIndex + 1}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • {seg.startTime}
                  </Typography>
                </Stack>
                <Typography variant="body2" fontSize="0.85rem">
                  "{seg.text}"
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {seg.speaker}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  )
}

function SimilarityMatrix({
  pairs,
  files,
  fileColors
}: {
  pairs: SimilarityPair[]
  files: { fileId: string; title: string }[]
  fileColors: Record<string, string>
}) {
  const getPairSimilarity = (id1: string, id2: string) => {
    if (id1 === id2) return 1
    const pair = pairs.find(
      p => (p.fileId1 === id1 && p.fileId2 === id2) || (p.fileId1 === id2 && p.fileId2 === id1)
    )
    return pair?.similarity || 0
  }

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" fontWeight={600} mb={2}>
        Matriz de Similaridade
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `auto repeat(${files.length}, 1fr)`, gap: 1 }}>
          {/* Header */}
          <Box />
          {files.map((file, idx) => (
            <Box
              key={file.fileId}
              sx={{
                p: 1,
                textAlign: 'center',
                bgcolor: fileColors[file.fileId],
                color: 'white',
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 600,
                minWidth: 80
              }}
            >
              {file.title.length > 15 ? file.title.slice(0, 15) + '...' : file.title}
            </Box>
          ))}

          {/* Rows */}
          {files.map((rowFile, rowIdx) => (
            <React.Fragment key={rowFile.fileId}>
              <Box
                sx={{
                  p: 1,
                  bgcolor: fileColors[rowFile.fileId],
                  color: 'white',
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 80
                }}
              >
                {rowFile.title.length > 12 ? rowFile.title.slice(0, 12) + '...' : rowFile.title}
              </Box>
              {files.map((colFile, colIdx) => {
                const similarity = getPairSimilarity(rowFile.fileId, colFile.fileId)
                const percentage = Math.round(similarity * 100)
                const isDiagonal = rowIdx === colIdx

                return (
                  <Box
                    key={colFile.fileId}
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      borderRadius: 1,
                      bgcolor: isDiagonal
                        ? '#f5f5f5'
                        : percentage >= 70
                        ? '#e8f5e9'
                        : percentage >= 40
                        ? '#fff3e0'
                        : '#ffebee',
                      color: isDiagonal
                        ? '#9e9e9e'
                        : percentage >= 70
                        ? '#2e7d32'
                        : percentage >= 40
                        ? '#f57c00'
                        : '#c62828',
                      fontWeight: 600,
                      fontSize: '0.85rem'
                    }}
                  >
                    {isDiagonal ? '—' : `${percentage}%`}
                  </Box>
                )
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Paper>
  )
}

export default function MultiComparisonPage({ files, onBack }: Props) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [data, setData] = useState<MultiTranscriptionComparison | null>(null)
  const [tabValue, setTabValue] = useState(0)
  const [expandedContradiction, setExpandedContradiction] = useState<string | null>(null)
  const { triggerNPS } = useNPS()

  // Mapa de cores para os arquivos
  const fileColors: Record<string, string> = {}
  files.forEach((file, idx) => {
    fileColors[file.fileId] = FILE_COLORS[idx % FILE_COLORS.length]
  })

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      setError(null)
      try {
        // Chama API real do backend monkee-ai-api
        const result = await compareTranscriptions(files.map(f => f.fileId))
        setData(result)
        // Dispara NPS após comparação carregada com sucesso
        triggerNPS('COMPARISON')
      } catch (e: any) {
        const friendlyError = contextualErrors.comparison(e)
        setError(friendlyError)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [files])

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        {/* Header com skeleton */}
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e0e0e0',
            p: 2,
            animation: `${fadeInUp} 0.3s ease-out`
          }}
        >
          <Box sx={{ maxWidth: 1400, mx: 'auto', px: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={onBack}>
                <ArrowBackIcon />
              </IconButton>
              <CompareArrowsIcon sx={{ color: '#1976d2' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Comparação de Transcrições
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {files.length} arquivos selecionados
                </Typography>
              </Box>
              <Skeleton variant="rounded" width={60} height={28} />
            </Stack>

            {/* File chips */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              {files.map((file, idx) => (
                <Chip
                  key={file.fileId}
                  label={file.title}
                  size="small"
                  sx={{
                    bgcolor: FILE_COLORS[idx % FILE_COLORS.length],
                    color: 'white',
                    fontWeight: 500,
                    mb: 1
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Conteúdo principal do loading */}
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
          {/* Cards de estatísticas (skeleton) */}
          <Grid container spacing={2} sx={{ mb: 4, animation: `${fadeInUp} 0.4s ease-out` }}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                  <Skeleton variant="text" width="50%" height={40} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width="70%" height={20} sx={{ mx: 'auto' }} />
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Área principal de loading */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              border: '1px solid #e0e0e0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              animation: `${fadeInUp} 0.5s ease-out`
            }}
          >
            {/* Ícone animado de comparação */}
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                position: 'relative'
              }}
            >
              <CompareArrowsIcon 
                sx={{ 
                  fontSize: 48, 
                  color: '#1976d2',
                  animation: `${pulse} 2s ease-in-out infinite`
                }} 
              />
              
              {/* Círculos orbitando */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '140%',
                  height: '140%',
                  animation: `${rotate} 8s linear infinite`
                }}
              >
                {files.map((file, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      position: 'absolute',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: FILE_COLORS[idx % FILE_COLORS.length],
                      top: idx === 0 ? 0 : idx === 1 ? '50%' : idx === 2 ? '100%' : '50%',
                      left: idx === 0 ? '50%' : idx === 1 ? '100%' : idx === 2 ? '50%' : 0,
                      transform: 'translate(-50%, -50%)',
                      boxShadow: `0 2px 8px ${FILE_COLORS[idx % FILE_COLORS.length]}50`
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Analisando transcrições...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Comparando {files.length} arquivos para identificar similaridades e divergências
            </Typography>

            {/* Barra de progresso */}
            <Box sx={{ width: '100%', maxWidth: 400, mb: 4 }}>
              <Box 
                sx={{ 
                  height: 6, 
                  bgcolor: '#e0e0e0', 
                  borderRadius: 3,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    bgcolor: '#1976d2',
                    borderRadius: 3,
                    animation: `${progressBar} 3s ease-in-out infinite`
                  }}
                />
              </Box>
            </Box>

            {/* Etapas do processo */}
            <Grid container spacing={3} sx={{ maxWidth: 600 }}>
              {[
                { icon: <SearchIcon />, label: 'Buscando padrões', color: '#1976d2' },
                { icon: <SyncAltIcon />, label: 'Comparando textos', color: '#9c27b0' },
                { icon: <WarningAmberIcon />, label: 'Identificando contradições', color: '#f57c00' },
                { icon: <AnalyticsIcon />, label: 'Gerando análise', color: '#00796b' }
              ].map((step, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      animation: `${bounce} 1.5s ease-in-out infinite`,
                      animationDelay: `${i * 0.2}s`
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: `${step.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                        color: step.color
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      {step.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Skeleton das tabs e conteúdo */}
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', mt: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
            <Box sx={{ borderBottom: '1px solid #e0e0e0', p: 1, display: 'flex', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rounded" width={120} height={36} />
              ))}
            </Box>
            <Box sx={{ p: 3 }}>
              <Skeleton variant="text" width="80%" height={24} sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {[1, 2].map((i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Skeleton variant="rounded" height={120} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
    )
  }

  if (error || !data) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 3 }}>
          Voltar
        </Button>
        
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            border: '1px solid #e0e0e0',
            bgcolor: '#fafafa',
            maxWidth: 500,
            mx: 'auto'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 40, color: '#f44336' }} />
          </Box>
          
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {error?.title || 'Erro ao comparar transcrições'}
          </Typography>
          
          <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
            {error?.message || 'Não foi possível carregar a comparação.'}
          </Typography>
          
          {error?.suggestion && (
            <Typography variant="body2" sx={{ color: '#888', mb: 3, fontStyle: 'italic' }}>
              💡 {error.suggestion}
            </Typography>
          )}
          
          {error?.canRetry && (
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
              sx={{ mt: 1 }}
            >
              Tentar novamente
            </Button>
          )}
        </Paper>
      </Box>
    )
  }

  const contradictionsBySeverity = {
    high: data.contradictions.filter(c => c.severity === 'high'),
    medium: data.contradictions.filter(c => c.severity === 'medium'),
    low: data.contradictions.filter(c => c.severity === 'low')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={onBack}>
              <ArrowBackIcon />
            </IconButton>
            <CompareArrowsIcon sx={{ color: '#1976d2' }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={600}>
                  Comparação de Transcrições
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        O que é a Comparação de Transcrições?
                      </Typography>
                      <Typography variant="caption" component="p" sx={{ mb: 1 }}>
                        Esta ferramenta analisa múltiplas transcrições para identificar:
                      </Typography>
                      <Typography variant="caption" component="p">• <strong>Contradições:</strong> Informações conflitantes entre os relatos</Typography>
                      <Typography variant="caption" component="p">• <strong>Coincidências:</strong> Trechos com conteúdo similar</Typography>
                      <Typography variant="caption" component="p">• <strong>Similaridade:</strong> Percentual de alinhamento entre arquivos</Typography>
                      <Typography variant="caption" component="p">• <strong>Tópicos:</strong> Assuntos em comum e exclusivos</Typography>
                    </Box>
                  }
                  arrow
                  placement="bottom-start"
                >
                  <IconButton size="small" sx={{ color: '#666' }}>
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <BetaBadge />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {files.length} arquivos selecionados
              </Typography>
            </Box>
            <Tooltip title="Similaridade geral calculada pela média ponderada entre todos os pares de arquivos">
              <Box>
                <SimilarityBadge value={data.overallSimilarity} />
              </Box>
            </Tooltip>
          </Stack>

          {/* File chips */}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
            {files.map((file, idx) => (
              <Chip
                key={file.fileId}
                label={file.title}
                size="small"
                sx={{
                  bgcolor: fileColors[file.fileId],
                  color: 'white',
                  fontWeight: 500,
                  mb: 1
                }}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Statistics Overview */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Tooltip title="Média geral de similaridade entre todos os pares de transcrições analisados" arrow>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {Math.round(data.overallSimilarity * 100)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Similaridade Geral
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Tooltip title="Número de trechos onde duas ou mais transcrições apresentam conteúdo similar ou idêntico" arrow>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h4" fontWeight={700} color="#2e7d32">
                  {data.statistics.matchingCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Trechos Coincidentes
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Tooltip title="Pontos onde as transcrições apresentam informações conflitantes ou divergentes sobre o mesmo assunto" arrow>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h4" fontWeight={700} color="#d32f2f">
                  {data.statistics.contradictionCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contradições
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Tooltip title="Total de segmentos de fala analisados em todas as transcrições" arrow>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h4" fontWeight={700} color="text.primary">
                  {data.statistics.totalSegments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Segmentos Analisados
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{ borderBottom: '1px solid #e0e0e0' }}
          >
            <Tab
              icon={<WarningAmberIcon />}
              iconPosition="start"
              label={`Contradições (${data.contradictions.length})`}
            />
            <Tab
              icon={<CheckCircleIcon />}
              iconPosition="start"
              label={`Coincidências (${data.matchingSegments.length})`}
            />
            <Tab icon={<GridViewIcon />} iconPosition="start" label="Matriz" />
            <Tab icon={<TopicIcon />} iconPosition="start" label="Tópicos" />
            <Tab 
              icon={<AutoAwesomeIcon />} 
              iconPosition="start" 
              label="Resumo IA" 
              sx={{ '& .MuiTab-iconWrapper': { color: '#9c27b0' } }}
            />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {/* Contradictions Tab */}
            {tabValue === 0 && (
              <Box>
                {/* Info Tip */}
                <Alert 
                  severity="info" 
                  icon={<LightbulbOutlinedIcon />}
                  sx={{ mb: 3, bgcolor: '#fff3e0', border: '1px solid #ffe0b2' }}
                >
                  <Typography variant="body2">
                    <strong>Contradições</strong> são pontos onde os relatos apresentam informações conflitantes 
                    sobre o mesmo assunto. Cada contradição é classificada por <strong>severidade</strong>: 
                    Alta (conflito direto), Média (divergência moderada) ou Baixa (variações menores).
                  </Typography>
                </Alert>

                {data.contradictions.length === 0 ? (
                  <Alert severity="success" icon={<CheckCircleIcon />}>
                    Nenhuma contradição identificada entre as transcrições!
                  </Alert>
                ) : (
                  <>
                    {/* Summary by severity */}
                    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                      {contradictionsBySeverity.high.length > 0 && (
                        <Chip
                          icon={<ErrorOutlineIcon />}
                          label={`${contradictionsBySeverity.high.length} Alta${contradictionsBySeverity.high.length > 1 ? 's' : ''}`}
                          sx={{ bgcolor: '#ffebee', color: '#c62828' }}
                        />
                      )}
                      {contradictionsBySeverity.medium.length > 0 && (
                        <Chip
                          label={`${contradictionsBySeverity.medium.length} Média${contradictionsBySeverity.medium.length > 1 ? 's' : ''}`}
                          sx={{ bgcolor: '#fff3e0', color: '#f57c00' }}
                        />
                      )}
                      {contradictionsBySeverity.low.length > 0 && (
                        <Chip
                          label={`${contradictionsBySeverity.low.length} Baixa${contradictionsBySeverity.low.length > 1 ? 's' : ''}`}
                          sx={{ bgcolor: '#e8f5e9', color: '#388e3c' }}
                        />
                      )}
                    </Stack>

                    {data.contradictions.map(contradiction => (
                      <ContradictionCard
                        key={contradiction.id}
                        contradiction={contradiction}
                        expanded={expandedContradiction === contradiction.id}
                        onToggle={() =>
                          setExpandedContradiction(
                            expandedContradiction === contradiction.id ? null : contradiction.id
                          )
                        }
                        fileColors={fileColors}
                      />
                    ))}
                  </>
                )}
              </Box>
            )}

            {/* Matching Segments Tab */}
            {tabValue === 1 && (
              <Box>
                {/* Info Tip */}
                <Alert 
                  severity="info" 
                  icon={<LightbulbOutlinedIcon />}
                  sx={{ mb: 3, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9' }}
                >
                  <Typography variant="body2">
                    <strong>Coincidências</strong> são trechos onde duas ou mais transcrições apresentam 
                    conteúdo similar. O <strong>percentual de similaridade</strong> indica o grau de 
                    correspondência entre os textos. Valores acima de 80% indicam relatos praticamente idênticos.
                  </Typography>
                </Alert>

                {data.matchingSegments.length === 0 ? (
                  <Alert severity="info">
                    Nenhum trecho coincidente identificado entre as transcrições.
                  </Alert>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Encontrados <strong>{data.matchingSegments.length}</strong> grupos de
                        segmentos com conteúdo similar entre as transcrições.
                      </Typography>
                    </Alert>
                    {data.matchingSegments.map((group, idx) => (
                      <MatchingSegmentGroup
                        key={idx}
                        segments={group}
                        fileColors={fileColors}
                      />
                    ))}
                  </>
                )}
              </Box>
            )}

            {/* Matrix Tab */}
            {tabValue === 2 && (
              <Box>
                {/* Info Tip */}
                <Alert 
                  severity="info" 
                  icon={<LightbulbOutlinedIcon />}
                  sx={{ mb: 3, bgcolor: '#e3f2fd', border: '1px solid #bbdefb' }}
                >
                  <Typography variant="body2">
                    A <strong>Matriz de Similaridade</strong> mostra o percentual de correspondência entre 
                    cada par de arquivos. Cores verdes indicam alta similaridade, amarelas indicam moderada, 
                    e vermelhas indicam baixa. Use esta visão para identificar quais relatos são mais 
                    alinhados entre si.
                  </Typography>
                </Alert>

                <SimilarityMatrix
                  pairs={data.similarityMatrix}
                  files={data.files}
                  fileColors={fileColors}
                />

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    Detalhes por Par
                  </Typography>
                  <Grid container spacing={2}>
                    {data.similarityMatrix.map((pair, idx) => {
                      const file1 = data.files.find(f => f.fileId === pair.fileId1)
                      const file2 = data.files.find(f => f.fileId === pair.fileId2)
                      return (
                        <Grid item xs={12} md={6} key={idx}>
                          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: fileColors[pair.fileId1]
                                }}
                              />
                              <Typography variant="body2" fontWeight={500} noWrap>
                                {file1?.title}
                              </Typography>
                              <CompareArrowsIcon sx={{ fontSize: 16, color: '#999' }} />
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: fileColors[pair.fileId2]
                                }}
                              />
                              <Typography variant="body2" fontWeight={500} noWrap>
                                {file2?.title}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <SimilarityBadge value={pair.similarity} />
                              <Typography variant="caption" color="text.secondary">
                                {pair.matchingSegments} segmentos similares
                              </Typography>
                            </Stack>
                            {pair.divergentTopics.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="error">
                                  Divergências: {pair.divergentTopics.join(', ')}
                                </Typography>
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      )
                    })}
                  </Grid>
                </Box>
              </Box>
            )}

            {/* Topics Tab */}
            {tabValue === 3 && (
              <Box>
                {/* Info Tip */}
                <Alert 
                  severity="info" 
                  icon={<LightbulbOutlinedIcon />}
                  sx={{ mb: 3, bgcolor: '#f3e5f5', border: '1px solid #ce93d8' }}
                >
                  <Typography variant="body2">
                    <strong>Tópicos Comuns</strong> são assuntos mencionados em todas as transcrições, 
                    indicando pontos centrais da narrativa. <strong>Tópicos Exclusivos</strong> aparecem 
                    apenas em um arquivo, podendo indicar informações complementares ou perspectivas únicas.
                  </Typography>
                </Alert>

                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Tópicos Comuns
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
                  {data.commonTopics.map((topic, idx) => (
                    <Chip
                      key={idx}
                      label={topic}
                      icon={<TopicIcon />}
                      sx={{ mb: 1, bgcolor: '#e3f2fd' }}
                    />
                  ))}
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Tópicos Exclusivos por Arquivo
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(data.uniqueTopicsByFile).map(([fileId, topics]) => {
                    const file = data.files.find(f => f.fileId === fileId)
                    return (
                      <Grid item xs={12} md={4} key={fileId}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: fileColors[fileId],
                            borderLeftWidth: 4
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} mb={1}>
                            {file?.title || fileId}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {topics.map((topic, idx) => (
                              <Chip
                                key={idx}
                                label={topic}
                                size="small"
                                variant="outlined"
                                sx={{ mb: 0.5, fontSize: '0.7rem' }}
                              />
                            ))}
                          </Stack>
                        </Paper>
                      </Grid>
                    )
                  })}
                </Grid>
              </Box>
            )}

            {/* AI Summary Tab */}
            {tabValue === 4 && (
              <Box>
                {/* AI Summary Header */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    background: 'linear-gradient(135deg, #f3e5f5 0%, #e8eaf6 100%)',
                    border: '1px solid #ce93d8'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: '#9c27b0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <AutoAwesomeIcon sx={{ color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        Resumo Gerado por IA
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Análise automatizada das {files.length} transcrições selecionadas, identificando padrões, 
                        pontos críticos e recomendações.
                      </Typography>
                    </Box>
                    <Tooltip title="Este resumo é gerado automaticamente por inteligência artificial e deve ser revisado por um profissional">
                      <IconButton size="small">
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>

                {/* Executive Summary */}
                <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <LightbulbOutlinedIcon sx={{ color: '#f57c00' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Síntese Executiva
                    </Typography>
                  </Stack>
                  <Typography variant="body2" paragraph>
                    A análise comparativa das {files.length} transcrições revela uma <strong>similaridade geral 
                    de {Math.round(data.overallSimilarity * 100)}%</strong> entre os relatos. Foram identificados{' '}
                    <strong>{data.statistics.matchingCount} pontos de convergência</strong> e{' '}
                    <strong>{data.statistics.contradictionCount} divergências</strong> que merecem atenção.
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Os depoimentos apresentam consistência nos seguintes tópicos principais:{' '}
                    <strong>{data.commonTopics.slice(0, 3).join(', ')}</strong>
                    {data.commonTopics.length > 3 && ` e mais ${data.commonTopics.length - 3} outros`}.
                  </Typography>
                  {data.contradictions.filter(c => c.severity === 'high').length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Atenção:</strong> Foram identificadas{' '}
                        {data.contradictions.filter(c => c.severity === 'high').length} contradições de alta 
                        severidade que requerem análise aprofundada.
                      </Typography>
                    </Alert>
                  )}
                </Paper>

                {/* Key Findings */}
                <Grid container spacing={3}>
                  {/* Consistency Analysis */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', height: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                        <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Análise de Consistência
                        </Typography>
                      </Stack>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Pontos de Concordância
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Os relatos convergem principalmente sobre: sequência temporal dos eventos, 
                            identificação dos envolvidos principais e descrição do local.
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Nível de Confiabilidade
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={data.overallSimilarity * 100}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: data.overallSimilarity >= 0.7 ? '#2e7d32' : '#f57c00'
                              }
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {data.overallSimilarity >= 0.7 ? 'Alta' : data.overallSimilarity >= 0.4 ? 'Moderada' : 'Baixa'} consistência entre os relatos
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Divergence Analysis */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', height: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                        <WarningAmberIcon sx={{ color: '#f57c00' }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Pontos de Atenção
                        </Typography>
                      </Stack>
                      <Stack spacing={1.5}>
                        {data.contradictions.slice(0, 3).map((c, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: SEVERITY_CONFIG[c.severity].bgColor,
                              borderLeft: `3px solid ${SEVERITY_CONFIG[c.severity].color}`
                            }}
                          >
                            <Typography variant="body2" fontWeight={500}>
                              {c.topic}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {c.description}
                            </Typography>
                          </Box>
                        ))}
                        {data.contradictions.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            + {data.contradictions.length - 3} outras divergências identificadas
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Recommendations */}
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #1976d2', bgcolor: '#e3f2fd' }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                        <LightbulbOutlinedIcon sx={{ color: '#1976d2' }} />
                        <Typography variant="subtitle1" fontWeight={600} color="#1976d2">
                          Recomendações
                        </Typography>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="body2" fontWeight={700} color="#1976d2">1.</Typography>
                            <Typography variant="body2">
                              <strong>Investigar contradições de alta severidade</strong> — Priorize a análise dos 
                              {data.contradictions.filter(c => c.severity === 'high').length} pontos críticos 
                              identificados.
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="body2" fontWeight={700} color="#1976d2">2.</Typography>
                            <Typography variant="body2">
                              <strong>Validar trechos coincidentes</strong> — Os {data.statistics.matchingCount} pontos 
                              de convergência podem ser utilizados como base factual.
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Typography variant="body2" fontWeight={700} color="#1976d2">3.</Typography>
                            <Typography variant="body2">
                              <strong>Analisar tópicos exclusivos</strong> — Verifique informações mencionadas 
                              apenas em um dos relatos para possível complementação.
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Disclaimer */}
                <Alert severity="info" sx={{ mt: 3 }} icon={<AutoAwesomeIcon />}>
                  <Typography variant="body2">
                    <strong>Nota:</strong> Este resumo foi gerado automaticamente por inteligência artificial 
                    com base na análise textual das transcrições. A IA pode não capturar nuances contextuais 
                    ou informações não-verbais. Recomenda-se sempre a validação por um profissional qualificado.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
